import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { CurrentLangService } from '@gfe/core/services/common/current-lang.service';
import { EntityTypeLabels } from '@gfe/core/services/gbe-error-parser/entity-ref-labels';
import { EntityRef, MessagePart } from '@gfe/core/services/gbe-error-parser/warning.model';

import { displayedPartText } from './finding-display';

type RefSegment = { kind: 'ref'; ref: EntityRef } | { kind: 'separator'; text: string };

/**
 * A finding's message with its references, durations and values picked out. Not interactive: the
 * row around it owns the click. It takes its colour from the row, which `muted` keeps every part to.
 */
@Component({
  selector: 'app-finding-message',
  imports: [TranslatePipe],
  templateUrl: './finding-message.component.html',
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block leading-[1.55] [overflow-wrap:anywhere]',
    '[class.line-through]': 'muted()'
  }
})
export class FindingMessageComponent {
  readonly parts = input.required<MessagePart[] | undefined>();
  readonly body = input.required<string>();
  readonly sameDay = input<string | null>(null);
  readonly typeLabels = input.required<EntityTypeLabels>();
  /** Struck through, every part in the row's colour: a finding that no longer holds. */
  readonly muted = input<boolean>(false);

  private readonly currentLang = inject(CurrentLangService).currentLang;

  protected readonly shown = computed(() => {
    const list = new Intl.ListFormat(this.currentLang() || undefined, {
      style: 'long',
      type: 'conjunction'
    });
    return (this.parts() ?? []).map((part) => ({
      part,
      text: displayedPartText(part, this.sameDay()),
      refSegments: part.kind === 'refs' ? refSegments(part.refs, list) : []
    }));
  });
}

/**
 * The chips with the words the presenter joins their names with, so "R1, R2 and R3" keeps its
 * conjunction once each name is a chip.
 */
function refSegments(refs: EntityRef[], list: Intl.ListFormat): RefSegment[] {
  return list
    .formatToParts(refs.map((_, index) => String(index)))
    .map((part) =>
      part.type === 'element'
        ? { kind: 'ref', ref: refs[Number(part.value)] }
        : { kind: 'separator', text: part.value }
    );
}
